import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, IconButton, Grid, Chip,
    CircularProgress, Tooltip, Card, CardContent, CardActions,
    Skeleton, Snackbar, Alert, TableContainer, Table,
    TableHead, TableRow, TableCell, TableBody, Button
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = 'https://tbl.zerdegroup.kz';

// Типы для сессий WhatsApp
interface WhatsAppSession {
    id: string;
    status: 'initializing' | 'scan_qr' | 'ready' | 'disconnected' | 'reconnecting' | 'auth_failure';
    ready: boolean;
    qrCodeUrl: string | null;
    last_used: string | null;
    created_at: string;
}

interface LogEntry {
    id: number;
    session_id: string | null;
    action: string;
    phone: string | null;
    login: string | null;
    status: string;
    message: string | null;
    created_at: string;
}

// Стили для статусов
const statusConfig: Record<string, { color: string; bgColor: string; label: string }> = {
    ready: { color: '#16a34a', bgColor: 'rgba(22, 163, 74, 0.1)', label: 'Готов' },
    scan_qr: { color: '#ca8a04', bgColor: 'rgba(202, 138, 4, 0.1)', label: 'Сканируйте QR' },
    initializing: { color: '#2563eb', bgColor: 'rgba(37, 99, 235, 0.1)', label: 'Инициализация' },
    disconnected: { color: '#dc2626', bgColor: 'rgba(220, 38, 38, 0.1)', label: 'Отключен' },
    reconnecting: { color: '#ea580c', bgColor: 'rgba(234, 88, 12, 0.1)', label: 'Переподключение' },
    auth_failure: { color: '#dc2626', bgColor: 'rgba(220, 38, 38, 0.1)', label: 'Ошибка авторизации' }
};

const WhatsAppBots: React.FC = () => {
    // State для сессий
    const [sessions, setSessions] = useState<WhatsAppSession[]>([]);
    const [loading, setLoading] = useState(true);
    const [addingSession, setAddingSession] = useState(false);

    // State для логов
    const [logs, setLogs] = useState<LogEntry[]>([]);

    // State для уведомлений
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
        open: false,
        message: '',
        severity: 'info'
    });

    const showNotification = (message: string, severity: 'success' | 'error' | 'info' = 'info') => {
        setSnackbar({ open: true, message, severity });
    };

    // Загрузка сессий
    const loadSessions = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/api/whatsapp/sessions`);
            if (response.data.success) {
                setSessions(response.data.sessions);
            }
        } catch (err) {
            console.error('Ошибка загрузки сессий:', err);
        }
    }, []);

    // Загрузка логов
    const loadLogs = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/whatsapp/logs?limit=100`);
            if (response.data.success) {
                setLogs(response.data.logs);
            }
        } catch (err) {
            console.error('Ошибка загрузки логов:', err);
        }
    };

    // Добавление новой сессии
    const handleAddSession = async () => {
        setAddingSession(true);
        try {
            const response = await axios.post(`${API_URL}/api/whatsapp/sessions`);
            if (response.data.success) {
                showNotification(`Сессия ${response.data.session.id} создана`, 'success');
                loadSessions();
                loadLogs();
            }
        } catch (err) {
            console.error('Ошибка создания сессии:', err);
            showNotification('Ошибка создания сессии', 'error');
        } finally {
            setAddingSession(false);
        }
    };

    // Удаление сессии
    const handleDeleteSession = async (sessionId: string) => {
        try {
            const response = await axios.delete(`${API_URL}/api/whatsapp/sessions/${sessionId}`);
            if (response.data.success) {
                setSessions(prev => prev.filter(s => s.id !== sessionId));
                showNotification(`Сессия ${sessionId} удалена`, 'success');
                loadLogs();
            }
        } catch (err) {
            console.error('Ошибка удаления сессии:', err);
            showNotification('Ошибка удаления сессии', 'error');
        }
    };

    // Форматирование даты
    const formatDate = (dateString: string | null) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Загрузка при монтировании
    useEffect(() => {
        const load = async () => {
            setLoading(true);
            await loadSessions();
            await loadLogs();
            setLoading(false);
        };
        load();
    }, [loadSessions]);

    // Автообновление для отслеживания QR-кодов
    useEffect(() => {
        const hasInitializing = sessions.some(s => s.status === 'initializing' || s.status === 'scan_qr');

        if (hasInitializing) {
            const interval = setInterval(() => {
                loadSessions();
            }, 2000); // Обновление каждые 2 секунды

            return () => clearInterval(interval);
        }
    }, [sessions, loadSessions]);

    return (
        <Box>
            {/* Секция сессий */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid #e5e7eb' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#374151' }}>
                        Активные сессии
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Обновить">
                            <IconButton size="small" onClick={() => { loadSessions(); loadLogs(); }} disabled={loading}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Добавить сессию">
                            <IconButton
                                size="small"
                                onClick={handleAddSession}
                                disabled={addingSession || loading}
                                sx={{ color: '#25D366' }}
                            >
                                {addingSession ? <CircularProgress size={18} /> : <AddIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {loading ? (
                    <Grid container spacing={2}>
                        {[1, 2, 3].map(i => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <Skeleton variant="rounded" height={220} />
                            </Grid>
                        ))}
                    </Grid>
                ) : sessions.length === 0 ? (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 4,
                            border: '1px dashed #d1d5db',
                            borderRadius: 2,
                            color: '#9ca3af'
                        }}
                    >
                        <WhatsAppIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                        <Typography variant="body2">Нет активных сессий</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Нажмите + для создания новой
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {sessions.map(session => (
                            <Grid item xs={12} sm={6} md={4} key={session.id}>
                                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                    <CardContent sx={{ pb: 1 }}>
                                        {/* Заголовок карточки */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                            <Typography variant="body2" fontWeight={600} sx={{ color: '#374151', fontSize: 12 }}>
                                                {session.id}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={statusConfig[session.status]?.label || session.status}
                                                sx={{
                                                    background: statusConfig[session.status]?.bgColor,
                                                    color: statusConfig[session.status]?.color,
                                                    fontWeight: 600,
                                                    fontSize: 10,
                                                    height: 22
                                                }}
                                            />
                                        </Box>

                                        {/* QR-код область */}
                                        <Box
                                            sx={{
                                                background: '#fff',
                                                border: '1px solid #e5e7eb',
                                                minHeight: 200,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: 1.5,
                                                mb: 1.5,
                                                overflow: 'hidden',
                                                p: 1
                                            }}
                                        >
                                            {session.qrCodeUrl ? (
                                                // Отображение QR-кода
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <img
                                                        src={session.qrCodeUrl}
                                                        alt="QR Code"
                                                        style={{
                                                            width: '100%',
                                                            maxWidth: 180,
                                                            height: 'auto'
                                                        }}
                                                    />
                                                    <Typography variant="caption" display="block" sx={{ mt: 1, color: '#ca8a04' }}>
                                                        Отсканируйте в WhatsApp
                                                    </Typography>
                                                </Box>
                                            ) : session.status === 'ready' ? (
                                                <Box sx={{ textAlign: 'center', color: '#16a34a' }}>
                                                    <CheckIcon sx={{ fontSize: 48 }} />
                                                    <Typography variant="body2" fontWeight={600}>ПОДКЛЮЧЕНО</Typography>
                                                </Box>
                                            ) : session.status === 'disconnected' || session.status === 'auth_failure' ? (
                                                <Box sx={{ textAlign: 'center', color: '#dc2626' }}>
                                                    <ErrorIcon sx={{ fontSize: 48 }} />
                                                    <Typography variant="body2" fontWeight={600}>ОТКЛЮЧЕНО</Typography>
                                                </Box>
                                            ) : (
                                                <Box sx={{ textAlign: 'center', color: '#9ca3af' }}>
                                                    <CircularProgress size={40} sx={{ color: '#2563eb', mb: 1 }} />
                                                    <Typography variant="caption" display="block">Ожидание QR-кода...</Typography>
                                                </Box>
                                            )}
                                        </Box>

                                        {/* Мета-информация */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', color: '#6b7280', fontSize: 11 }}>
                                            <span>Создан:</span>
                                            <span>{formatDate(session.created_at)}</span>
                                        </Box>
                                    </CardContent>
                                    <CardActions sx={{ px: 2, pb: 1.5, pt: 0 }}>
                                        <Button
                                            fullWidth
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            startIcon={<DeleteIcon fontSize="small" />}
                                            onClick={() => handleDeleteSession(session.id)}
                                            sx={{ fontSize: 12 }}
                                        >
                                            Удалить
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Paper>

            {/* Секция логов */}
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e5e7eb' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#374151' }}>
                        Системный лог
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Последние 100 записей
                    </Typography>
                </Box>

                <TableContainer sx={{ maxHeight: 300, border: '1px solid #e5e7eb', borderRadius: 1 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ bgcolor: '#f9fafb', fontWeight: 600, py: 1 }}>Время</TableCell>
                                <TableCell sx={{ bgcolor: '#f9fafb', fontWeight: 600, py: 1 }}>Действие</TableCell>
                                <TableCell sx={{ bgcolor: '#f9fafb', fontWeight: 600, py: 1 }}>Сессия</TableCell>
                                <TableCell sx={{ bgcolor: '#f9fafb', fontWeight: 600, py: 1 }}>Телефон</TableCell>
                                <TableCell sx={{ bgcolor: '#f9fafb', fontWeight: 600, py: 1 }}>Статус</TableCell>
                                <TableCell sx={{ bgcolor: '#f9fafb', fontWeight: 600, py: 1 }}>Сообщение</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {logs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ textAlign: 'center', color: '#9ca3af', py: 4 }}>
                                        Нет записей в логе
                                    </TableCell>
                                </TableRow>
                            ) : (
                                logs.map(log => (
                                    <TableRow key={log.id} hover>
                                        <TableCell sx={{ py: 1, fontSize: 12, color: '#6b7280' }}>
                                            {formatDate(log.created_at)}
                                        </TableCell>
                                        <TableCell sx={{ py: 1, fontSize: 12 }}>
                                            {log.action}
                                        </TableCell>
                                        <TableCell sx={{ py: 1, fontSize: 12, color: '#2563eb' }}>
                                            {log.session_id || '—'}
                                        </TableCell>
                                        <TableCell sx={{ py: 1, fontSize: 12 }}>
                                            {log.phone || '—'}
                                        </TableCell>
                                        <TableCell sx={{ py: 1 }}>
                                            <Chip
                                                size="small"
                                                label={log.status}
                                                sx={{
                                                    fontSize: 10,
                                                    height: 20,
                                                    background: log.status === 'success' ? 'rgba(22, 163, 74, 0.1)' :
                                                        log.status === 'error' ? 'rgba(220, 38, 38, 0.1)' : 'rgba(37, 99, 235, 0.1)',
                                                    color: log.status === 'success' ? '#16a34a' :
                                                        log.status === 'error' ? '#dc2626' : '#2563eb'
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ py: 1, fontSize: 12, color: '#6b7280' }}>
                                            {log.message || '—'}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default WhatsAppBots;
